import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import type { TextUnitData } from '@/features/content/useComponent';

import { HighlightOverlay, type MeasuredAnnotation } from './HighlightOverlay';
import { NoteGutter } from './NoteGutter';
import { SelectionPopover } from './SelectionPopover';
import type {
  ActiveAnnotationSelection,
  Annotation,
  AnnotationDraft,
  AnnotationRect,
  AnnotationReference,
  AnnotationTextColor,
} from './annotationTypes';
import { getAnnotationContainerId } from './annotationTypes';
import { captureSelectionQuote } from './text/selectionCapture';
import { extractPlainText } from './text/extractPlainText';
import { getTextOffsetRange } from './text/textOffsetRange';
import { resolveTextQuote } from './text/textQuoteResolver';
import styles from './AnnotationLayer.module.css';

interface InlineNoteLayout {
  connectorWidth: number;
  left: number;
  width: number;
}

interface AnnotationLayerProps {
  activeAnnotation: AnnotationReference | null;
  activeSelection: ActiveAnnotationSelection | null;
  annotations: Annotation[];
  children: ReactNode;
  highlightColor: string;
  noteTextColor: AnnotationTextColor;
  onCreateAnnotation: (
    draft: AnnotationDraft & { contentUnitId: string },
  ) => Promise<unknown> | unknown;
  onDeleteAnnotation: (args: {
    annotation: Annotation;
    contentUnitId: string;
  }) => Promise<void> | void;
  onSelectionChange: (selection: ActiveAnnotationSelection | null) => void;
  onSetActiveAnnotation: (reference: AnnotationReference | null) => void;
  onUpdateAnnotation: (args: {
    annotation: Annotation;
    contentUnitId: string;
  }) => Promise<void> | void;
  showAnnotations: boolean;
  unit: TextUnitData;
}

function clearBrowserSelection() {
  window.getSelection()?.removeAllRanges();
}

function toRelativeRect(rect: DOMRect, rootRect: DOMRect): AnnotationRect {
  return {
    height: rect.height,
    left: rect.left - rootRect.left,
    top: rect.top - rootRect.top,
    width: rect.width,
  };
}

function isPointInsideRect(x: number, y: number, rect: AnnotationRect) {
  return (
    x >= rect.left &&
    x <= rect.left + rect.width &&
    y >= rect.top &&
    y <= rect.top + rect.height
  );
}

function getInlineNoteLayout(rootElement: HTMLElement): InlineNoteLayout | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const notesLane = document.querySelector(
    '[data-content-notes-lane="true"]',
  ) as HTMLElement | null;

  if (!notesLane) {
    return null;
  }

  const rootRect = rootElement.getBoundingClientRect();
  const laneRect = notesLane.getBoundingClientRect();

  if (laneRect.width < 240 || laneRect.left < rootRect.right + 24) {
    return null;
  }

  return {
    connectorWidth: 0,
    left: laneRect.left - rootRect.left,
    width: laneRect.width,
  };
}

function getMeasuredAnnotations(
  contentElement: HTMLElement,
  rootElement: HTMLElement,
  annotations: Annotation[],
  plainText: string,
): MeasuredAnnotation[] {
  const rootRect = rootElement.getBoundingClientRect();

  return annotations.flatMap((annotation) => {
    const resolution = resolveTextQuote(plainText, annotation.quote);

    if (resolution.status !== 'resolved') {
      return [];
    }

    const range = getTextOffsetRange(
      contentElement,
      resolution.startOffset,
      resolution.endOffset,
    );

    if (!range) {
      return [];
    }

    const rects = Array.from(range.getClientRects())
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .map((rect) => toRelativeRect(rect, rootRect));

    if (rects.length === 0) {
      return [];
    }

    return [
      {
        annotation,
        rects,
        resolution,
      } satisfies MeasuredAnnotation,
    ];
  });
}

export function AnnotationLayer({
  activeAnnotation,
  activeSelection,
  annotations,
  children,
  highlightColor,
  noteTextColor,
  onCreateAnnotation,
  onDeleteAnnotation,
  onSelectionChange,
  onSetActiveAnnotation,
  onUpdateAnnotation,
  showAnnotations,
  unit,
}: AnnotationLayerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [cardRight, setCardRight] = useState(0);
  const [inlineNoteLayout, setInlineNoteLayout] =
    useState<InlineNoteLayout | null>(null);
  const [layerWidth, setLayerWidth] = useState(0);
  const [proseRight, setProseRight] = useState(0);
  const [measuredAnnotations, setMeasuredAnnotations] = useState<MeasuredAnnotation[]>(
    [],
  );
  const [openNoteIds, setOpenNoteIds] = useState<Set<string>>(() => new Set());
  const plainText = useMemo(() => extractPlainText(unit.html).text, [unit.html]);
  const selectionForUnit =
    activeSelection?.contentUnitId === unit.id ? activeSelection : null;
  const showInlineNotes =
    showAnnotations &&
    inlineNoteLayout !== null &&
    measuredAnnotations.some(({ annotation }) => annotation.kind === 'note');

  useEffect(() => {
    const noteIds = new Set(
      annotations
        .filter((annotation) => annotation.kind === 'note')
        .map((annotation) => annotation.id),
    );

    setOpenNoteIds((current) => {
      const next = new Set([...current].filter((id) => noteIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [annotations]);

  useEffect(() => {
    if (
      !activeAnnotation ||
      activeAnnotation.contentUnitId !== unit.id ||
      !annotations.some(
        (annotation) =>
          annotation.id === activeAnnotation.annotationId &&
          annotation.kind === 'note',
      )
    ) {
      return;
    }

    setOpenNoteIds((current) => {
      if (current.has(activeAnnotation.annotationId)) {
        return current;
      }

      return new Set(current).add(activeAnnotation.annotationId);
    });
  }, [activeAnnotation, annotations, unit.id]);

  useEffect(() => {
    const rootElement = rootRef.current;
    const contentElement = rootElement?.querySelector('.lesson-content') as HTMLElement | null;

    if (!rootElement || !contentElement) {
      setCardRight(0);
      setInlineNoteLayout(null);
      setLayerWidth(0);
      setProseRight(0);
      setMeasuredAnnotations([]);
      return;
    }

    let measurementFrame: number | null = null;
    const scrollListenerOptions = {
      capture: true,
      passive: true,
    } satisfies AddEventListenerOptions;

    const updateMeasurements = () => {
      const rootRect = rootElement.getBoundingClientRect();
      const cardElement = rootElement.closest(
        '[data-reading-card="true"]',
      ) as HTMLElement | null;

      setMeasuredAnnotations(
        getMeasuredAnnotations(contentElement, rootElement, annotations, plainText),
      );
      setCardRight(
        cardElement
          ? cardElement.getBoundingClientRect().right - rootRect.left
          : 0,
      );
      setInlineNoteLayout(getInlineNoteLayout(rootElement));
      setLayerWidth(rootRect.width);
      setProseRight(contentElement.getBoundingClientRect().right - rootRect.left);
    };

    const scheduleMeasurements = () => {
      if (measurementFrame !== null) {
        return;
      }

      measurementFrame = window.requestAnimationFrame(() => {
        measurementFrame = null;
        updateMeasurements();
      });
    };

    updateMeasurements();

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            scheduleMeasurements();
          });

    resizeObserver?.observe(rootElement);
    resizeObserver?.observe(contentElement);

    window.addEventListener('resize', scheduleMeasurements);
    window.addEventListener('scroll', scheduleMeasurements, scrollListenerOptions);

    return () => {
      if (measurementFrame !== null) {
        window.cancelAnimationFrame(measurementFrame);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasurements);
      window.removeEventListener('scroll', scheduleMeasurements, scrollListenerOptions);
    };
  }, [annotations, plainText]);

  useEffect(() => {
    const rootElement = rootRef.current;
    const contentElement = rootElement?.querySelector('.lesson-content') as HTMLElement | null;

    if (!rootElement || !contentElement) {
      return;
    }

    const updateSelection = (event?: MouseEvent | PointerEvent) => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed) {
        if (selectionForUnit) {
          onSelectionChange(null);
        }

        if (event && showAnnotations) {
          const rootRect = rootElement.getBoundingClientRect();
          const clickX = event.clientX - rootRect.left;
          const clickY = event.clientY - rootRect.top;
          const clickedAnnotation = [...measuredAnnotations]
            .reverse()
            .find(({ rects }) =>
              rects.some((rect) => isPointInsideRect(clickX, clickY, rect)),
            )?.annotation;

          if (clickedAnnotation) {
            const reference = {
              annotationId: clickedAnnotation.id,
              contentUnitId: clickedAnnotation.contentUnitId,
            } satisfies AnnotationReference;
            const isActive =
              activeAnnotation?.annotationId === clickedAnnotation.id &&
              activeAnnotation.contentUnitId === clickedAnnotation.contentUnitId;

            if (clickedAnnotation.kind === 'note') {
              if (inlineNoteLayout) {
                onSetActiveAnnotation(isActive ? null : reference);
                return;
              }

              const isOpen = openNoteIds.has(clickedAnnotation.id);

              setOpenNoteIds((current) => {
                const next = new Set(current);

                if (next.has(clickedAnnotation.id)) {
                  next.delete(clickedAnnotation.id);
                } else {
                  next.add(clickedAnnotation.id);
                }

                return next;
              });
              onSetActiveAnnotation(isOpen ? null : reference);
              return;
            }

            onSetActiveAnnotation(isActive ? null : reference);
            return;
          }

          if (activeAnnotation?.contentUnitId === unit.id) {
            onSetActiveAnnotation(null);
          }
        }

        return;
      }

      const captured = captureSelectionQuote(contentElement, selection);

      if (!captured) {
        return;
      }

      onSetActiveAnnotation(null);
      onSelectionChange({
        contentUnitId: unit.id,
        draft: {
          contentUnitId: unit.id,
          id: captured.id,
          note: '',
          quote: captured.quote,
          timestamp: captured.timestamp,
        },
        rect: toRelativeRect(
          captured.rect,
          rootElement.getBoundingClientRect(),
        ),
        selectedText: captured.selectedText,
      });
    };

    const handleMouseUp = (event: MouseEvent) => {
      window.requestAnimationFrame(() => updateSelection(event));
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') {
        return;
      }

      window.requestAnimationFrame(() => updateSelection(event));
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key.startsWith('Arrow') || event.key === 'Shift') {
        window.requestAnimationFrame(() => updateSelection());
      }
    };

    contentElement.addEventListener('mouseup', handleMouseUp);
    contentElement.addEventListener('pointerup', handlePointerUp);
    contentElement.addEventListener('keyup', handleKeyUp);

    return () => {
      contentElement.removeEventListener('mouseup', handleMouseUp);
      contentElement.removeEventListener('pointerup', handlePointerUp);
      contentElement.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    activeAnnotation,
    inlineNoteLayout,
    measuredAnnotations,
    onSelectionChange,
    onSetActiveAnnotation,
    openNoteIds,
    selectionForUnit,
    showAnnotations,
    unit.id,
  ]);

  return (
    <div className={styles.layer} id={getAnnotationContainerId(unit.id)} ref={rootRef}>
      <div className={styles.contentShell}>{children}</div>

      {showAnnotations ? (
        <>
          <HighlightOverlay
            activeAnnotation={activeAnnotation}
            annotations={measuredAnnotations}
            cardRight={cardRight}
            highlightColor={highlightColor}
            layerWidth={layerWidth}
            noteTextColor={noteTextColor}
            proseRight={proseRight}
            onCloseNoteAnnotation={(annotation) => {
              setOpenNoteIds((current) => {
                if (!current.has(annotation.id)) {
                  return current;
                }

                const next = new Set(current);
                next.delete(annotation.id);
                return next;
              });
            }}
            onDeleteAnnotation={async (annotation) => {
              await onDeleteAnnotation({
                annotation,
                contentUnitId: unit.id,
              });
            }}
            onOpenAnnotation={onSetActiveAnnotation}
            onToggleNoteAnnotation={(annotation) => {
              const reference = {
                annotationId: annotation.id,
                contentUnitId: annotation.contentUnitId,
              } satisfies AnnotationReference;
              const isOpen = openNoteIds.has(annotation.id);

              setOpenNoteIds((current) => {
                const next = new Set(current);

                if (next.has(annotation.id)) {
                  next.delete(annotation.id);
                  return next;
                }

                next.add(annotation.id);
                return next;
              });
              onSetActiveAnnotation(isOpen ? null : reference);
            }}
            onUpdateAnnotation={async (annotation) => {
              await onUpdateAnnotation({
                annotation,
                contentUnitId: unit.id,
              });
            }}
            openNoteIds={openNoteIds}
            showNoteMarkers={!showInlineNotes}
          />

          {showInlineNotes ? (
            <NoteGutter
              activeAnnotation={activeAnnotation}
              highlightColor={highlightColor}
              layout={inlineNoteLayout}
              noteTextColor={noteTextColor}
              notes={measuredAnnotations.filter(
                ({ annotation }) => annotation.kind === 'note',
              )}
              onDeleteAnnotation={async (annotation) => {
                await onDeleteAnnotation({
                  annotation,
                  contentUnitId: unit.id,
                });
              }}
              onSetActiveAnnotation={onSetActiveAnnotation}
              onUpdateAnnotation={async (annotation) => {
                await onUpdateAnnotation({
                  annotation,
                  contentUnitId: unit.id,
                });
              }}
            />
          ) : null}
        </>
      ) : null}

      <SelectionPopover
        onCreateHighlight={async (selection) => {
          await onCreateAnnotation({
            ...selection.draft,
            contentUnitId: unit.id,
          });
          clearBrowserSelection();
          onSelectionChange(null);
        }}
        onCreateNote={async (selection, note) => {
          await onCreateAnnotation({
            ...selection.draft,
            contentUnitId: unit.id,
            note,
            textColor: noteTextColor,
          });
          clearBrowserSelection();
          onSelectionChange(null);
        }}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            clearBrowserSelection();
            onSelectionChange(null);
          }
        }}
        open={Boolean(selectionForUnit)}
        selection={selectionForUnit}
      />
    </div>
  );
}
